import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../utils/api';
import { getSectorBySlug as getPrioritySectorBySlug } from '../../data/prioritySectors';
import { fetchNewsList } from '../../utils/api/newsApi';
import { fetchExpertsList } from '../../utils/api/expertApi';
import SectorHero from '../../components/sector/SectorHero';
import SubSectorSection from '../../components/sector/SubSectorSection';
import SectorEvents from '../../components/sector/SectorEvents';
import SectorNews from '../../components/sector/SectorNews';
import SectorExperts from '../../components/sector/SectorExperts';
import SectorGallery from '../../components/sector/SectorGallery';
import SectorDownloads from '../../components/sector/SectorDownloads';
import SectorGoals from '../../components/sector/SectorGoals';
import SectorFeedback from '../../components/sector/SectorFeedback';
import { getEvents } from '../../utils/api/eventApi';
import './Sector.css';

const labels = {
  ne: {
    notFoundTitle: 'क्षेत्र भेटिएन',
    notFoundText: 'क्षमा गर्नुहोस्, यो प्राथमिकता क्षेत्र भेटिएन।',
    backToSectors: 'प्राथमिकता क्षेत्रहरूमा फर्किनुहोस्',
    home: 'गृहपृष्ठ',
    relatedTitle: 'सम्बन्धित क्षेत्रहरू',
    relatedSubtitle: 'भोलिको इटहरीका अन्य प्राथमिकता क्षेत्रहरू हेर्नुहोस्।',
    relatedButton: 'सबै प्राथमिकता क्षेत्रहरू हेर्नुहोस्',
  },
  en: {
    notFoundTitle: 'Sector Not Found',
    notFoundText: 'Sorry, this priority area could not be found.',
    backToSectors: 'Back to Priority Areas',
    home: 'Home',
    relatedTitle: 'Related Sectors',
    relatedSubtitle: "Explore other Priority Areas for Bholiko Itahari's Vision 2050.",
    relatedButton: 'Back to Priority Areas',
  },
};

const SectorDetails = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [sector, setSector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const prioritySector = getPrioritySectorBySlug(slug);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/sectors')
      .then((res) => {
        if (!mounted) return;
        const data = Array.isArray(res.data) ? res.data : [];
        const mapped = data.map((s) => ({
          id: s._id,
          title: s.name || '',
          description: s.description || '',
          // preserve Unicode names as slugs if server doesn't provide one
          slug: s.slug || s.name || s._id,
          icon: typeof s.icon === 'string' && s.icon.length > 1 ? s.icon : 'Building2',
          subSectors: Array.isArray(s.tags) ? s.tags.map((t) => ({ title: t, description: '' })) : [],
          news: [],
          gallery: [],
          downloads: [],
          goals: [],
          order: typeof s.order === 'number' ? s.order : 999,
        }));

        const found = mapped.find((m) => m.slug === slug);
        if (found) setSector(found);
        else setSector(null);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || 'Failed to load sector');
        setLoading(false);
      });

    return () => { mounted = false; };
  }, [slug]);
  const t = labels[language] || labels.ne;
  const [realNews, setRealNews] = useState([]);
  const [experts, setExperts] = useState([]);
  const [events, setEvents] = useState([]);
  const [fetchedSubSectors, setFetchedSubSectors] = useState([]);
  const [loadingSubSectors, setLoadingSubSectors] = useState(false);

  useEffect(() => {
    if (!sector) return;
    let isMounted = true;
    getEvents()
      .then((data) => {
        if (!isMounted) return;
        const all = Array.isArray(data) ? data : (data?.data ?? data?.events ?? []);
        const sectorId = String(sector.id || sector._id || '');
        const sectorTitle = (sector.title || sector.name || '').trim().toLowerCase();
        const sectorSlug = (sector.slug || '').trim().toLowerCase();

        const filteredEvents = all.filter((ev) => {
          if (!ev) return false;
          const eventCat = (ev.category || '').trim();
          const eventSectorId = String(ev.sectorId || ev.sector_id || (typeof ev.sector === 'object' ? ev.sector?._id : ev.sector) || '');
          const eventSectorName = (typeof ev.sector === 'object' ? ev.sector?.name : '').trim().toLowerCase();

          // 1. Match by Sector ID
          if (sectorId && (eventSectorId === sectorId || eventCat === sectorId)) {
            return true;
          }
          // 2. Match by Sector Title / Name
          if (sectorTitle && (eventCat.toLowerCase() === sectorTitle || eventSectorName === sectorTitle)) {
            return true;
          }
          // 3. Match by Sector Slug
          if (sectorSlug && eventCat.toLowerCase() === sectorSlug) {
            return true;
          }
          return false;
        });

        setEvents(filteredEvents);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('Failed to load sector events:', err);
        setEvents([]);
      });

    return () => {
      isMounted = false;
    };
  }, [sector?.id, sector?.title, sector?.slug]);

  useEffect(() => {
    if (sector?.title) {
      fetchNewsList({ sector: sector.title })
        .then(data => {
          const articles = Array.isArray(data) ? data : (data?.data ?? []);
          setRealNews(articles);
        })
        .catch(() => {});
    }
  }, [sector?.title]);

  useEffect(() => {
    if (sector?.title) {
      fetchExpertsList({ sector: sector.title })
        .then(data => {
          const list = Array.isArray(data) ? data : (data?.data ?? []);
          setExperts(list);
        })
        .catch(() => {});
    }
  }, [sector?.title]);

  useEffect(() => {
    if (!sector?.id) return;
    setLoadingSubSectors(true);
    api.get(`/sectors/${sector.id}/subsectors`)
      .then((res) => {
        const subs = Array.isArray(res.data) ? res.data : [];
        const mapped = subs.map((s) => ({ title: s.name || s.title || '', description: s.description || '' }));
        setFetchedSubSectors(mapped);
        setLoadingSubSectors(false);
      })
      .catch(() => {
        setFetchedSubSectors([]);
        setLoadingSubSectors(false);
      });
  }, [sector?.id]);

  if (loading) {
    return (
      <main className="sector-not-found page-enter">
        <div className="container">
          <div className="not-found-content">
            <div className="not-found-icon">…</div>
            <h1>Loading…</h1>
          </div>
        </div>
      </main>
    );
  }

  // Show error / 404 if sector not found
  if (!sector) {
    return (
      <main className="sector-not-found page-enter">
        <div className="container">
          <div className="not-found-content">
            <div className="not-found-icon">!</div>
            <h1>{t.notFoundTitle}</h1>
            <p>{t.notFoundText}</p>
            <div className="not-found-actions">
              <Link to="/sectors" className="btn btn-primary">
                {t.backToSectors}
              </Link>
              <Link to="/" className="btn btn-secondary">
                {t.home}
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={`sector-details page-enter sector-details--${language}`}>
      <SectorHero sector={sector} prioritySector={prioritySector} language={language} />
      {
        (() => {
          const fallback = sector.subSectors || [];
          const normalizedFallback = fallback.length > 0 && typeof fallback[0] === 'string'
            ? fallback.map((s) => ({ title: s, description: '' }))
            : fallback;
          return (
            <SubSectorSection subSectors={fetchedSubSectors.length > 0 ? fetchedSubSectors : normalizedFallback} language={language} />
          );
        })()
      }
      <SectorEvents events={events} language={language} />
      <SectorNews news={realNews.length > 0 ? realNews : sector.news} language={language} />
      <SectorExperts experts={experts} language={language} />
      <SectorGallery gallery={sector.gallery} language={language} />
      <SectorDownloads downloads={sector.downloads} language={language} />
      <SectorGoals goals={sector.goals} language={language} />
      <SectorFeedback sectorTitle={sector.title} language={language} />

      {/* Related Sectors Section */}
      <section className="sector-details__related">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t.relatedTitle}</h2>
            <p className="section-subtitle">
              {t.relatedSubtitle}
            </p>
          </div>
          <div className="related-cta">
            <Link to="/sectors" className="btn btn-primary">
              {t.relatedButton}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SectorDetails;